import "./index.css";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import originsJson from "./public/data/origins.json";
import universitiesJson from "./public/data/universities.json";
import type { TransferRow } from "./data/tranuData";
import { TRANSFER_ROWS, YEAR_TOTALS } from "./data/tranuData";
import { ResourceGrid, SiteChrome } from "./SiteChrome";
import { mainSiteUrl, pageTitle } from "./site";

type DataRow = {
  year: string;
  university: string;
  subject: string;
  subjectEn: string;
  intakeByInstituteCode: Record<string, number>;
};

type ParsedDataset = { rows: DataRow[]; instituteOrder: string[] };
type DataTab = "senior" | "fyfd";
type BrowseMode = "by_university" | "by_origin";

type AppRoute =
  | { kind: "root" }
  | { kind: "tranu" }
  | { kind: "list"; tab: DataTab; mode: BrowseMode }
  | { kind: "universityOriginSummary"; tab: DataTab }
  | { kind: "universityOriginDetail"; tab: DataTab; university: string }
  | { kind: "subjectsByUniversity"; tab: DataTab; university: string }
  | { kind: "tableByUniversity"; tab: DataTab; university: string; subjectKey: string }
  | { kind: "instituteOverview"; tab: DataTab; originName: string }
  | { kind: "tableByOrigin"; tab: DataTab; originName: string; receivingUniversity: string }
  | { kind: "originUniversityYearly"; tab: DataTab; originName: string; receivingUniversity: string };

const PATH_SENIOR_INTAKE = "senior_actual_intake";
const PATH_FYFD_INTAKE = "fyfd_actual_intake";
const PATH_ORIGIN_AT_UNIVERSITY = "at_university";

const DEFAULT_META_DESCRIPTION =
  "整合教資會紀錄：按大學、課程及原就讀副學位院校劃分的高年級／一年級非聯招入學人數，一眼比較過往收生分佈，助您規劃大學銜接。@nonjupas.hk";
const ACTIVATION_CODE = "4540";
const ACTIVATION_STORAGE_KEY = "ugc_stats_activation_code";

function isLikelySearchBot(userAgent: string): boolean {
  return /(googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|sogou|crawler|spider|bot)/i.test(
    userAgent,
  );
}

type UniversitySlugItem = { full: string; short: string };
type OriginSlugItem = { code: string; full: string; short: string };
type SlugMaps = {
  uniFullToShort: Map<string, string>;
  uniShortToFull: Map<string, string>;
  originFullToShort: Map<string, string>;
  originShortToFull: Map<string, string>;
};

function buildSlugMaps(
  universities: { items: UniversitySlugItem[] },
  origins: { items: OriginSlugItem[] },
): SlugMaps {
  const uniFullToShort = new Map<string, string>();
  const uniShortToFull = new Map<string, string>();
  for (const it of universities.items) {
    uniFullToShort.set(it.full, it.short);
    uniShortToFull.set(it.short, it.full);
    uniShortToFull.set(it.short.toLowerCase(), it.full);
  }
  const originFullToShort = new Map<string, string>();
  const originShortToFull = new Map<string, string>();
  for (const it of origins.items) {
    originFullToShort.set(it.full, it.short);
    originShortToFull.set(it.short, it.full);
    originShortToFull.set(it.short.toLowerCase(), it.full);
  }
  return { uniFullToShort, uniShortToFull, originFullToShort, originShortToFull };
}

const SLUG_MAPS = buildSlugMaps(universitiesJson, originsJson);

function pathSegmentForUniversity(fullZh: string): string {
  return encodeURIComponent(SLUG_MAPS.uniFullToShort.get(fullZh) ?? fullZh);
}
function pathSegmentForOrigin(fullZh: string): string {
  return encodeURIComponent(SLUG_MAPS.originFullToShort.get(fullZh) ?? fullZh);
}
function parseUniversityPathSegment(segment: string): string {
  const raw = safeDecode(segment);
  return SLUG_MAPS.uniShortToFull.get(raw) ?? SLUG_MAPS.uniShortToFull.get(raw.toLowerCase()) ?? raw;
}
function parseOriginPathSegment(segment: string): string {
  const raw = safeDecode(segment).trim();
  return SLUG_MAPS.originShortToFull.get(raw) ?? SLUG_MAPS.originShortToFull.get(raw.toLowerCase()) ?? raw;
}

function setMetaContent(selector: string, content: string) {
  const el = document.querySelector<HTMLMetaElement>(selector);
  if (el) el.content = content;
}
function setCanonicalHref(href: string) {
  const el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (el) el.href = href;
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else inQuotes = !inQuotes;
      continue;
    }
    if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
}

function toNumber(value: string): number {
  const normalized = value.replace(/\s+/g, "");
  if (!normalized || normalized === "-") return 0;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseSeniorYearCsv(csvText: string): ParsedDataset {
  const lines = csvText.split(/\r?\n/).filter(Boolean);
  const headerLine = lines[0];
  if (lines.length < 2 || !headerLine) return { rows: [], instituteOrder: [] };
  const header = parseCsvLine(headerLine);
  const instituteCodes = header.slice(6).map(v => v.trim());
  const rows: DataRow[] = [];
  for (const line of lines.slice(1)) {
    const cells = parseCsvLine(line);
    if (cells.length < 6) continue;
    const year = cells[0];
    const university = cells[1] || cells[2];
    const subject = cells[3] || cells[4];
    const subjectEn = cells[4] || cells[3] || "";
    if (!year || !university || !subject || subject === "小計") continue;
    const intakeByInstituteCode: Record<string, number> = {};
    instituteCodes.forEach((code, index) => {
      const count = toNumber(cells[6 + index] ?? "");
      if (count > 0) intakeByInstituteCode[code] = count;
    });
    rows.push({ year, university, subject, subjectEn, intakeByInstituteCode });
  }
  return { rows, instituteOrder: instituteCodes };
}

function parseFyfdCsv(csvText: string): ParsedDataset {
  const lines = csvText.split(/\r?\n/).filter(Boolean);
  const headerLine = lines[0];
  if (lines.length < 2 || !headerLine) return { rows: [], instituteOrder: [] };
  const header = parseCsvLine(headerLine);
  const instituteCodes = header.slice(7).map(v => v.trim());
  const rows: DataRow[] = [];
  for (const line of lines.slice(1)) {
    const cells = parseCsvLine(line);
    if (cells.length < 7) continue;
    const year = cells[0];
    const university = cells[1] || cells[2];
    const subject = cells[3] || cells[4];
    const subjectEn = cells[4] || cells[3] || "";
    if (!year || !university || !subject || subject === "小計") continue;
    const intakeByInstituteCode: Record<string, number> = {};
    instituteCodes.forEach((code, index) => {
      const count = toNumber(cells[7 + index] ?? "");
      if (count > 0) intakeByInstituteCode[code] = count;
    });
    rows.push({ year, university, subject, subjectEn, intakeByInstituteCode });
  }
  return { rows, instituteOrder: instituteCodes };
}

function isCsvHttpResponse(res: Response): boolean {
  const ct = (res.headers.get("content-type") ?? "").toLowerCase();
  if (ct.includes("text/html")) return false;
  return ct.includes("csv") || ct.includes("text/plain");
}

function parseInstituteMap(csvText: string): Record<string, string> {
  const lines = csvText.split(/\r?\n/).filter(Boolean);
  const map: Record<string, string> = {};
  for (const line of lines) {
    const cells = parseCsvLine(line);
    if (cells.length < 2) continue;
    const code = cells[0]?.trim() ?? "";
    const nameZh = cells[1]?.trim();
    if (nameZh && /^\d+$/.test(code)) map[code] = nameZh;
  }
  return map;
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function slugifyAscii(value: string): string {
  const asciiOnly = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x00-\x7F]/g, "");
  const slug = asciiOnly
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug) return slug;
  const compact = value.replace(/\s+/g, "");
  const unicodeSlug = [...compact]
    .map(ch => ch.codePointAt(0)?.toString(16) ?? "")
    .filter(Boolean)
    .join("-");
  return unicodeSlug ? `u${unicodeSlug}` : "u";
}

function buildSubjectSlugMaps(rows: DataRow[], university: string): {
  subjectToSlug: Map<string, string>;
  slugToSubject: Map<string, string>;
} {
  const subjectToSlug = new Map<string, string>();
  const slugToSubject = new Map<string, string>();
  const subjectEntries = [
    ...new Map(
      rows
        .filter(r => r.university === university)
        .map(r => [r.subject, r.subjectEn] as const),
    ).entries(),
  ];
  const used = new Set<string>();
  for (const [subject, subjectEn] of subjectEntries) {
    const base = slugifyAscii(subjectEn || subject);
    let candidate = base;
    let index = 2;
    while (used.has(candidate)) {
      candidate = `${base}-${index.toString()}`;
      index += 1;
    }
    used.add(candidate);
    subjectToSlug.set(subject, candidate);
    slugToSubject.set(candidate, subject);
  }
  return { subjectToSlug, slugToSubject };
}

function normalizePathname(pathname: string): string {
  let path = pathname;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

function pathPrefixTab(t: DataTab): string {
  return t === "fyfd" ? PATH_FYFD_INTAKE : PATH_SENIOR_INTAKE;
}

function normalizeLegacyPathParts(parts: string[]): string[] {
  const head = parts[0];
  if (head !== "senior" && head !== "fyfd") return parts;
  const tabPrefix = head === "fyfd" ? PATH_FYFD_INTAKE : PATH_SENIOR_INTAKE;
  const rest = parts.slice(1);
  if (rest.length === 0) return [tabPrefix, "by_university"];
  if (rest[0] === "college") {
    const tail = rest.slice(1);
    if (tail.length === 0) return [tabPrefix, "by_origin"];
    return [tabPrefix, "by_origin", ...tail];
  }
  return [tabPrefix, "by_university", ...rest];
}

function parseRouteFromPathname(pathname: string): AppRoute {
  const path = normalizePathname(pathname);
  let parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return { kind: "root" };
  if (parts.length === 1 && parts[0] === "tranu") return { kind: "tranu" };
  parts = normalizeLegacyPathParts(parts);
  const p0 = parts[0];
  if (p0 !== PATH_SENIOR_INTAKE && p0 !== PATH_FYFD_INTAKE) return { kind: "root" };
  const tab: DataTab = p0 === PATH_FYFD_INTAKE ? "fyfd" : "senior";
  const p1 = parts[1];
  if (p1 === "by_university_origin") {
    if (parts.length === 2) return { kind: "universityOriginSummary", tab };
    if (parts.length === 3)
      return { kind: "universityOriginDetail", tab, university: parseUniversityPathSegment(parts[2] ?? "") };
    return { kind: "root" };
  }
  if (p1 !== "by_university" && p1 !== "by_origin") return { kind: "root" };
  if (p1 === "by_university") {
    if (parts.length === 2) return { kind: "list", tab, mode: "by_university" };
    if (parts.length === 3)
      return { kind: "subjectsByUniversity", tab, university: parseUniversityPathSegment(parts[2] ?? "") };
    const university = parseUniversityPathSegment(parts[2] ?? "");
    const subjectKey = parts.slice(3).map(s => safeDecode(s)).join("/");
    return { kind: "tableByUniversity", tab, university, subjectKey };
  }
  if (parts.length === 2) return { kind: "list", tab, mode: "by_origin" };
  if (parts.length === 3)
    return { kind: "instituteOverview", tab, originName: parseOriginPathSegment(parts[2] ?? "") };
  if (parts.length >= 5 && parts[3] === PATH_ORIGIN_AT_UNIVERSITY) {
    const originName = parseOriginPathSegment(parts[2] ?? "");
    const receivingUniversity = parseUniversityPathSegment(parts.slice(4).map(s => safeDecode(s)).join("/"));
    return { kind: "originUniversityYearly", tab, originName, receivingUniversity };
  }
  const originName = parseOriginPathSegment(parts[2] ?? "");
  const receivingUniversity = parseUniversityPathSegment(parts.slice(3).map(s => safeDecode(s)).join("/"));
  return { kind: "tableByOrigin", tab, originName, receivingUniversity };
}

function pathForList(tab: DataTab, mode: BrowseMode): string {
  return `/${pathPrefixTab(tab)}/${mode}`;
}
function pathForSubjectsByUniversity(tab: DataTab, university: string): string {
  return `/${pathPrefixTab(tab)}/by_university/${pathSegmentForUniversity(university)}`;
}
function pathForTableByUniversity(tab: DataTab, university: string, subjectSlug: string): string {
  return `/${pathPrefixTab(tab)}/by_university/${pathSegmentForUniversity(university)}/${subjectSlug}`;
}
function pathForInstituteOverview(tab: DataTab, originName: string): string {
  return `/${pathPrefixTab(tab)}/by_origin/${pathSegmentForOrigin(originName)}`;
}
function pathForTableByOrigin(tab: DataTab, originName: string, receivingUniversity: string): string {
  return `/${pathPrefixTab(tab)}/by_origin/${pathSegmentForOrigin(originName)}/${pathSegmentForUniversity(receivingUniversity)}`;
}
function pathForOriginUniversityYearly(tab: DataTab, originName: string, receivingUniversity: string): string {
  return `/${pathPrefixTab(tab)}/by_origin/${pathSegmentForOrigin(originName)}/${PATH_ORIGIN_AT_UNIVERSITY}/${pathSegmentForUniversity(receivingUniversity)}`;
}
function pathForUniversityOriginSummary(tab: DataTab): string {
  return `/${pathPrefixTab(tab)}/by_university_origin`;
}
function pathForUniversityOriginDetail(tab: DataTab, university: string): string {
  return `/${pathPrefixTab(tab)}/by_university_origin/${pathSegmentForUniversity(university)}`;
}

function listBrowseLabel(tab: DataTab, mode: BrowseMode): string {
  if (mode === "by_university") return tab === "fyfd" ? "一年級（以大學劃分）" : "高年級（以大學劃分）";
  return tab === "fyfd" ? "一年級（以原就讀院校劃分）" : "高年級（以原就讀院校劃分）";
}

function intakeShortLabel(tab: DataTab): string {
  return tab === "fyfd" ? "一年級" : "高年級";
}

function headingForRoute(
  route: AppRoute,
  extras: { subject?: string; originName?: string } = {},
): string {
  switch (route.kind) {
    case "root":
      return "入學統計";
    case "tranu":
      return "本地生轉校人數";
    case "list":
      return route.mode === "by_university"
        ? `${intakeShortLabel(route.tab)}入學人數（以大學劃分）`
        : `${intakeShortLabel(route.tab)}入學人數（以原就讀院校劃分）`;
    case "universityOriginSummary":
      return `${intakeShortLabel(route.tab)}入學 · 原就讀院校分布`;
    case "universityOriginDetail":
      return `${route.university} · ${intakeShortLabel(route.tab)}原就讀院校分布`;
    case "subjectsByUniversity":
      return `${route.university} · ${intakeShortLabel(route.tab)}課程`;
    case "tableByUniversity":
      return `${route.university} · ${extras.subject || route.subjectKey}（${intakeShortLabel(route.tab)}）`;
    case "instituteOverview":
      return `${extras.originName || route.originName} · ${intakeShortLabel(route.tab)}升學`;
    case "tableByOrigin":
      return `${extras.originName || route.originName} · ${route.receivingUniversity}（${intakeShortLabel(route.tab)}）`;
    case "originUniversityYearly":
      return `${extras.originName || route.originName} · ${route.receivingUniversity} · 按年圖表`;
  }
}

function resolveOriginToCode(originSegment: string, instituteMap: Record<string, string>): string | null {
  const trimmed = originSegment.trim();
  if (/^\d+$/.test(trimmed) && trimmed in instituteMap) return trimmed;
  for (const [code, name] of Object.entries(instituteMap)) {
    if (name === trimmed) return code;
  }
  return null;
}

type TableModel = {
  years: string[];
  codes: string[];
  matrix: Record<string, Record<string, number>>;
  columnTotals: Record<string, number>;
};

function buildTableModel(
  rows: DataRow[],
  instituteOrder: string[],
  university: string,
  subject: string,
): TableModel | null {
  const filtered = rows.filter(r => r.university === university && r.subject === subject);
  if (filtered.length === 0) return null;
  const yearSet = new Set(filtered.map(r => r.year));
  const years = [...yearSet].sort((a, b) => b.localeCompare(a));
  const matrix: Record<string, Record<string, number>> = {};
  for (const code of instituteOrder) {
    matrix[code] = {};
    for (const y of years) matrix[code][y] = 0;
  }
  for (const row of filtered) {
    for (const [code, n] of Object.entries(row.intakeByInstituteCode)) {
      if (!matrix[code]) {
        matrix[code] = {};
        for (const y of years) matrix[code][y] = 0;
      }
      matrix[code][row.year] = (matrix[code][row.year] ?? 0) + n;
    }
  }
  const visibleCodes = instituteOrder.filter(code => years.some(y => (matrix[code]?.[y] ?? 0) > 0));
  const columnTotals: Record<string, number> = {};
  for (const y of years) columnTotals[y] = 0;
  for (const code of visibleCodes) {
    for (const y of years) columnTotals[y] = (columnTotals[y] ?? 0) + (matrix[code]?.[y] ?? 0);
  }
  return { years, codes: visibleCodes, matrix, columnTotals };
}

type InstituteSectionModel = {
  university: string;
  years: string[];
  subjects: string[];
  matrix: Record<string, Record<string, number>>;
  columnTotals: Record<string, number>;
};

function buildInstitutePageModel(
  rows: DataRow[],
  instituteCode: string,
  universitiesOrdered: string[],
): InstituteSectionModel[] {
  const yearSet = new Set<string>();
  for (const r of rows) yearSet.add(r.year);
  const years = [...yearSet].sort((a, b) => b.localeCompare(a));
  const byUniversity = new Map<string, DataRow[]>();
  for (const r of rows) {
    const list = byUniversity.get(r.university) ?? [];
    list.push(r);
    byUniversity.set(r.university, list);
  }
  const sections: InstituteSectionModel[] = [];
  for (const university of universitiesOrdered) {
    const uniRows = byUniversity.get(university);
    if (!uniRows) continue;
    const subjectSet = new Set<string>();
    for (const r of uniRows) {
      if ((r.intakeByInstituteCode[instituteCode] ?? 0) > 0) subjectSet.add(r.subject);
    }
    if (subjectSet.size === 0) continue;
    const subjects = [...subjectSet].sort((a, b) => a.localeCompare(b));
    const matrix: Record<string, Record<string, number>> = {};
    for (const s of subjects) {
      matrix[s] = {};
      for (const y of years) matrix[s][y] = 0;
    }
    for (const r of uniRows) {
      const subjRow = matrix[r.subject];
      if (!subjRow) continue;
      subjRow[r.year] = (subjRow[r.year] ?? 0) + (r.intakeByInstituteCode[instituteCode] ?? 0);
    }
    const columnTotals: Record<string, number> = {};
    for (const y of years) columnTotals[y] = 0;
    for (const s of subjects) {
      for (const y of years) columnTotals[y] = (columnTotals[y] ?? 0) + (matrix[s]?.[y] ?? 0);
    }
    sections.push({ university, years, subjects, matrix, columnTotals });
  }
  return sections;
}

type OriginSliceModel = { code: string; name: string; count: number; pct: number; color: string };
type UniversityOriginYearModel = { year: string; total: number; slices: OriginSliceModel[] };

function originSliceColor(index: number): string {
  return `hsl(${((index * 47 + 19) % 360).toString()} 56% 48%)`;
}

function buildUniversityOriginByYear(
  rows: DataRow[],
  university: string,
  instituteMap: Record<string, string>,
): UniversityOriginYearModel[] {
  const uniRows = rows.filter(r => r.university === university);
  const years = [...new Set(uniRows.map(r => r.year))].sort((a, b) => b.localeCompare(a));
  const sections: UniversityOriginYearModel[] = [];
  for (const year of years) {
    const acc: Record<string, number> = {};
    for (const r of uniRows) {
      if (r.year !== year) continue;
      for (const [code, n] of Object.entries(r.intakeByInstituteCode)) {
        if (n <= 0) continue;
        acc[code] = (acc[code] ?? 0) + n;
      }
    }
    const pairs = Object.entries(acc)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
    const total = pairs.reduce((s, [, n]) => s + n, 0);
    if (total <= 0) continue;
    const slices: OriginSliceModel[] = pairs.map(([code, count], index) => ({
      code,
      name: instituteMap[code] ?? `院校 ${code}`,
      count,
      pct: (count / total) * 100,
      color: originSliceColor(index),
    }));
    sections.push({ year, total, slices });
  }
  return sections;
}

function buildOriginAtUniversityByYear(
  rows: DataRow[],
  originCode: string,
  receivingUniversity: string,
): UniversityOriginYearModel[] {
  const uniRows = rows.filter(r => r.university === receivingUniversity);
  const years = [...new Set(uniRows.map(r => r.year))].sort((a, b) => b.localeCompare(a));
  const sections: UniversityOriginYearModel[] = [];
  for (const year of years) {
    const bySubject: Record<string, number> = {};
    for (const r of uniRows) {
      if (r.year !== year) continue;
      const n = r.intakeByInstituteCode[originCode] ?? 0;
      if (n <= 0) continue;
      bySubject[r.subject] = (bySubject[r.subject] ?? 0) + n;
    }
    const pairs = Object.entries(bySubject)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
    const total = pairs.reduce((s, [, n]) => s + n, 0);
    if (total <= 0) continue;
    const slices: OriginSliceModel[] = pairs.map(([subject, count], index) => ({
      code: subject,
      name: subject,
      count,
      pct: (count / total) * 100,
      color: originSliceColor(index),
    }));
    sections.push({ year, total, slices });
  }
  return sections;
}

function pieSlicePath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const rad = Math.PI / 180;
  const x1 = cx + r * Math.cos(startDeg * rad);
  const y1 = cy + r * Math.sin(startDeg * rad);
  const x2 = cx + r * Math.cos(endDeg * rad);
  const y2 = cy + r * Math.sin(endDeg * rad);
  const delta = endDeg - startDeg;
  const largeArc = delta > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

function OriginPieChartInteractive({
  slices,
  total,
  ariaLabel,
}: {
  slices: OriginSliceModel[];
  total: number;
  ariaLabel: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);
  const size = 280;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  if (total <= 0 || slices.length === 0) {
    return (
      <div className="origin-pie-placeholder" role="img" aria-label={ariaLabel}>
        無數據
      </div>
    );
  }
  let angleDeg = -90;
  const paths = slices.map((s, i) => {
    const span = (s.count / total) * 360;
    const start = angleDeg;
    const end = angleDeg + span;
    angleDeg = end;
    const d = pieSlicePath(cx, cy, r, start, end);
    const dimmed = hover !== null && hover !== i;
    return (
      <path
        key={s.code}
        d={d}
        fill={s.color}
        stroke="rgba(255, 247, 245, 0.92)"
        strokeWidth={1.25}
        className="origin-pie-slice"
        style={{ cursor: "pointer", opacity: dimmed ? 0.38 : 1 }}
        onMouseEnter={() => setHover(i)}
        onMouseMove={e => setTipPos({ x: e.clientX, y: e.clientY })}
      />
    );
  });
  const hov = hover !== null && tipPos && slices[hover] ? slices[hover] : null;
  return (
    <div
      className="origin-pie-chart-host"
      onMouseLeave={() => {
        setHover(null);
        setTipPos(null);
      }}
    >
      <svg viewBox={`0 0 ${size} ${size}`} className="origin-pie-svg" role="img" aria-label={ariaLabel}>
        {paths}
      </svg>
      {hov ? (
        <div className="origin-pie-tooltip" style={{ left: tipPos!.x + 14, top: tipPos!.y + 14 }}>
          <span className="origin-pie-tooltip-title">{hov.name}</span>
          <span className="origin-pie-tooltip-meta">
            {hov.count.toLocaleString("zh-Hant")} 人 ·{" "}
            {(Math.round(hov.pct * 10) / 10).toLocaleString("zh-Hant", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            %
          </span>
        </div>
      ) : null}
    </div>
  );
}

function TranuYearBlock({ year, rows }: { year: TransferRow["year"]; rows: TransferRow[] }) {
  const yearTotal = YEAR_TOTALS[year];
  const sectionHeadingId = `tranu-year-${year.replace(/\//g, "-")}`;
  return (
    <section className="institute-uni-section tranu-year-section" aria-labelledby={sectionHeadingId}>
      <h3 id={sectionHeadingId} className="institute-uni-title tranu-year-heading">
        {year}
        <span className="tranu-year-meta">該學年小計 {yearTotal.toLocaleString("zh-Hant")} 人</span>
      </h3>
      <div className="tranu-year-body">
        <div className="tranu-column-headings tranu-transfer-grid">
          <span className="tranu-col-heading tranu-col-out">原就讀院校</span>
          <span className="tranu-col-heading-spacer" aria-hidden="true" />
          <span className="tranu-col-heading tranu-col-in">轉入院校</span>
        </div>
        <ul className="tranu-card-list">
          {rows.map((row, index) => (
            <li key={`${year}-${index.toString()}`} className="tranu-card tranu-transfer-grid">
              <div className="tranu-card-block tranu-card-from">
                <span className="tranu-card-uni">{row.fromUniversity}</span>
                <span className="tranu-card-program">{row.fromProgram}</span>
              </div>
              <div className="tranu-card-connector" aria-hidden="true">
                <span className="tranu-card-connector-arrow" />
              </div>
              <div className="tranu-card-block tranu-card-to">
                <span className="tranu-card-uni">{row.toUniversity}</span>
                <span className="tranu-card-program">{row.toProgram}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function YearDistributionBlock({
  ym,
  ariaPieLabel,
  firstColHeader,
}: {
  ym: UniversityOriginYearModel;
  ariaPieLabel: string;
  firstColHeader: string;
}) {
  return (
    <section className="uni-origin-section">
      <h3 className="institute-uni-title">學年 {ym.year}</h3>
      <div className="uni-origin-layout">
        <div className="origin-pie-wrap">
          <OriginPieChartInteractive slices={ym.slices} total={ym.total} ariaLabel={ariaPieLabel} />
          <p className="origin-pie-total">合計 {ym.total.toLocaleString("zh-Hant")} 人</p>
        </div>
        <div className="table-wrap">
          <table className="data-table data-table-origin-summary">
            <colgroup>
              <col className="data-col-institute" />
              <col className="data-col-year" />
              <col className="data-col-year" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="sticky-col">
                  {firstColHeader}
                </th>
                <th scope="col" className="num">
                  人數
                </th>
                <th scope="col" className="num">
                  佔比
                </th>
              </tr>
            </thead>
            <tbody>
              {ym.slices.map(s => (
                <tr key={s.code}>
                  <th scope="row" className="sticky-col row-label">
                    {s.name}
                  </th>
                  <td className="num">{s.count.toLocaleString("zh-Hant")}</td>
                  <td className="num">
                    {(Math.round(s.pct * 10) / 10).toLocaleString("zh-Hant", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                    %
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <th scope="row" className="sticky-col row-label">
                  總計
                </th>
                <td className="num">{ym.total.toLocaleString("zh-Hant")}</td>
                <td className="num">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function App() {
  const [route, setRoute] = useState<AppRoute>(() => parseRouteFromPathname(window.location.pathname));
  const dataTab: DataTab | null =
    route.kind === "root" || route.kind === "tranu" ? null : route.tab;
  const tab: DataTab = dataTab ?? "senior";
  const [rows, setRows] = useState<DataRow[]>([]);
  const [instituteOrder, setInstituteOrder] = useState<string[]>([]);
  const [instituteMap, setInstituteMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(() => parseRouteFromPathname(window.location.pathname).kind !== "root");
  const [error, setError] = useState("");
  const [activationInput, setActivationInput] = useState("");
  const [activationError, setActivationError] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [activationReady, setActivationReady] = useState(false);

  const navigate = useCallback((to: string) => {
    window.history.pushState(null, "", to);
    setRoute(parseRouteFromPathname(window.location.pathname));
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (isLikelySearchBot(ua)) {
      setIsUnlocked(true);
      setActivationReady(true);
      return;
    }
    const stored = localStorage.getItem(ACTIVATION_STORAGE_KEY);
    if (stored === ACTIVATION_CODE) setIsUnlocked(true);
    setActivationReady(true);
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(parseRouteFromPathname(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (dataTab === null) {
      setIsLoading(false);
      setError("");
      return;
    }
    let cancelled = false;
    async function loadData() {
      setIsLoading(true);
      try {
        let dataCsv: string;
        let instituteListCsvText: string;
        try {
          const dataUrl = dataTab === "fyfd" ? "/api/fyfd" : "/api/senior-year";
          const [dataRes, instituteListRes] = await Promise.all([
            fetch(dataUrl),
            fetch("/api/institute-list"),
          ]);
          if (!dataRes.ok || !instituteListRes.ok) throw new Error("API unavailable");
          if (!isCsvHttpResponse(dataRes) || !isCsvHttpResponse(instituteListRes)) throw new Error("API unavailable");
          [dataCsv, instituteListCsvText] = await Promise.all([dataRes.text(), instituteListRes.text()]);
        } catch {
          const dataPath = dataTab === "fyfd" ? "/data/fyfd.csv" : "/data/senior_year.csv";
          const [staticDataRes, staticInstituteRes] = await Promise.all([
            fetch(dataPath),
            fetch("/data/institute_list.csv"),
          ]);
          if (!staticDataRes.ok || !staticInstituteRes.ok)
            throw new Error(`Static data failed: ${staticDataRes.status} / ${staticInstituteRes.status}`);
          [dataCsv, instituteListCsvText] = await Promise.all([
            staticDataRes.text(),
            staticInstituteRes.text(),
          ]);
        }
        if (!cancelled) {
          const parsedData = dataTab === "fyfd" ? parseFyfdCsv(dataCsv) : parseSeniorYearCsv(dataCsv);
          setRows(parsedData.rows);
          setInstituteOrder(parsedData.instituteOrder);
          setInstituteMap(parseInstituteMap(instituteListCsvText));
          setError("");
        }
      } catch (loadError) {
        if (!cancelled)
          setError(loadError instanceof Error ? loadError.message : "Unknown error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void loadData();
    return () => {
      cancelled = true;
    };
  }, [dataTab]);

  const universities = useMemo(
    () => [...new Set(rows.map(row => row.university))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );
  const subjects = useMemo(() => {
    if (route.kind !== "subjectsByUniversity") return [];
    return [
      ...new Set(rows.filter(row => row.university === route.university).map(row => row.subject)),
    ].sort((a, b) => a.localeCompare(b));
  }, [rows, route]);
  const subjectSlugByName = useMemo(() => {
    if (route.kind !== "subjectsByUniversity") return new Map<string, string>();
    const { subjectToSlug } = buildSubjectSlugMaps(rows, route.university);
    return subjectToSlug;
  }, [rows, route]);

  const tableModel = useMemo(() => {
    if (route.kind !== "tableByUniversity") return null;
    const { slugToSubject } = buildSubjectSlugMaps(rows, route.university);
    const resolvedSubject =
      rows.some(r => r.university === route.university && r.subject === route.subjectKey)
        ? route.subjectKey
        : slugToSubject.get(route.subjectKey) ?? "";
    if (!resolvedSubject) return null;
    return buildTableModel(rows, instituteOrder, route.university, resolvedSubject);
  }, [rows, instituteOrder, route]);

  const routeTableSubject = useMemo(() => {
    if (route.kind !== "tableByUniversity") return "";
    if (rows.some(r => r.university === route.university && r.subject === route.subjectKey))
      return route.subjectKey;
    const { slugToSubject } = buildSubjectSlugMaps(rows, route.university);
    return slugToSubject.get(route.subjectKey) ?? "";
  }, [rows, route]);

  const instituteCodesWithData = useMemo(() => {
    const active = new Set<string>();
    for (const r of rows) {
      for (const [code, n] of Object.entries(r.intakeByInstituteCode)) {
        if (n > 0) active.add(code);
      }
    }
    return instituteOrder.filter(c => active.has(c));
  }, [rows, instituteOrder]);

  const institutesWithDataNames = useMemo(() => {
    const names = instituteCodesWithData
      .map(c => instituteMap[c])
      .filter((n): n is string => Boolean(n));
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [instituteCodesWithData, instituteMap]);

  const originCodeResolved = useMemo(() => {
    if (
      route.kind !== "instituteOverview" &&
      route.kind !== "tableByOrigin" &&
      route.kind !== "originUniversityYearly"
    )
      return null;
    return resolveOriginToCode(route.originName, instituteMap);
  }, [route, instituteMap]);

  const institutePageSections = useMemo(() => {
    if (!originCodeResolved) return null;
    if (route.kind !== "instituteOverview" && route.kind !== "tableByOrigin") return null;
    return buildInstitutePageModel(rows, originCodeResolved, universities);
  }, [rows, route, universities, originCodeResolved]);

  const originSingleSection = useMemo(() => {
    if (route.kind !== "tableByOrigin" || !institutePageSections) return null;
    return institutePageSections.find(s => s.university === route.receivingUniversity) ?? null;
  }, [route, institutePageSections]);

  const universityOriginDetailKey = route.kind === "universityOriginDetail" ? route.university : "";
  const universityOriginYearSections = useMemo(() => {
    if (!universityOriginDetailKey) return null;
    return buildUniversityOriginByYear(rows, universityOriginDetailKey, instituteMap);
  }, [rows, instituteMap, universityOriginDetailKey]);

  const originUniversityYearlyReceiving =
    route.kind === "originUniversityYearly" ? route.receivingUniversity : "";
  const originUniversityYearSections = useMemo(() => {
    if (route.kind !== "originUniversityYearly" || !originCodeResolved || !originUniversityYearlyReceiving)
      return null;
    return buildOriginAtUniversityByYear(rows, originCodeResolved, originUniversityYearlyReceiving);
  }, [rows, originCodeResolved, route.kind, originUniversityYearlyReceiving]);

  const tranuByYear = useMemo(() => {
    const grouped: Record<string, typeof TRANSFER_ROWS> = {};
    for (const row of TRANSFER_ROWS) {
      const list = grouped[row.year] ?? [];
      list.push(row);
      grouped[row.year] = list;
    }
    return grouped;
  }, []);
  const tranuOverallTotal = useMemo(
    () => Object.values(YEAR_TOTALS).reduce((sum, v) => sum + v, 0),
    [],
  );

  const originLinkName = useMemo(() => {
    if (
      route.kind !== "instituteOverview" &&
      route.kind !== "tableByOrigin" &&
      route.kind !== "originUniversityYearly"
    )
      return "";
    if (originCodeResolved) return instituteMap[originCodeResolved] ?? route.originName;
    return route.originName;
  }, [route, originCodeResolved, instituteMap]);

  const heading = useMemo(
    () => headingForRoute(route, { subject: routeTableSubject, originName: originLinkName }),
    [route, routeTableSubject, originLinkName],
  );

  useEffect(() => {
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const pathNorm = pathname === "/" || pathname === "" ? "/" : normalizePathname(pathname);
    const canonicalUrl = `${origin}${pathNorm}`;
    const fullTitle = pageTitle(heading);
    let description = DEFAULT_META_DESCRIPTION;
    if (route.kind === "tableByUniversity") {
      const intakeLabel = tab === "fyfd" ? "一年級入學" : "高年級入學";
      description = `${route.university}「${routeTableSubject || route.subjectKey}」— ${intakeLabel}各原就讀副學位／院校之非聯招入學人數。資料來源：教資會。@nonjupas.hk`;
    } else if (route.kind === "subjectsByUniversity") {
      const intakeLabel = tab === "fyfd" ? "一年級入學" : "高年級入學";
      description = `${route.university} — ${intakeLabel}選科：查看各科原就讀副學位院校之非聯招入學人數。@nonjupas.hk`;
    } else if (route.kind === "list") {
      const intakeShort = tab === "fyfd" ? "一年級" : "高年級";
      const browseShort = route.mode === "by_university" ? "以大學劃分" : "以原就讀院校劃分";
      description = `瀏覽教資會非聯招入學人數：${intakeShort}、${browseShort}。@nonjupas.hk`;
    } else if (route.kind === "instituteOverview") {
      const instName = originLinkName || route.originName;
      const intakeLabel = tab === "fyfd" ? "一年級入學" : "高年級入學";
      description = `原就讀「${instName}」— ${intakeLabel}升讀各教資會資助大學課程之非聯招人數（按大學、課程及學年）。@nonjupas.hk`;
    } else if (route.kind === "tableByOrigin") {
      const instName = originLinkName || route.originName;
      const intakeLabel = tab === "fyfd" ? "一年級入學" : "高年級入學";
      description = `原就讀「${instName}」入讀「${route.receivingUniversity}」— ${intakeLabel}各課程非聯招人數（按學年）。@nonjupas.hk`;
    } else if (route.kind === "originUniversityYearly") {
      const instName = originLinkName || route.originName;
      const intakeLabel = tab === "fyfd" ? "一年級入學" : "高年級入學";
      description = `原就讀「${instName}」入讀「${route.receivingUniversity}」— ${intakeLabel}按學年之課程人數占比。@nonjupas.hk`;
    } else if (route.kind === "universityOriginSummary") {
      const intakeShort = tab === "fyfd" ? "一年級" : "高年級";
      description = `按大學劃分：${intakeShort}非聯招入學原就讀院校分布（不分科目、按學年）。@nonjupas.hk`;
    } else if (route.kind === "universityOriginDetail") {
      const intakeShort = tab === "fyfd" ? "一年級" : "高年級";
      description = `「${route.university}」${intakeShort}非聯招入學：按學年之原就讀院校人數及占比（不分科目）。@nonjupas.hk`;
    } else if (route.kind === "tranu") {
      description = "2022/23 至 2024/25 學年按大學及課程劃分的教資會資助學士學位課程本地生轉校人數。@nonjupas.hk";
    } else if (route.kind === "root") description = DEFAULT_META_DESCRIPTION;
    document.title = fullTitle;
    setMetaContent('meta[property="og:title"]', fullTitle);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', fullTitle);
    setMetaContent('meta[name="twitter:description"]', description);
    setMetaContent('meta[name="description"]', description);
    setCanonicalHref(canonicalUrl);
  }, [route, tab, heading, instituteMap, originLinkName, routeTableSubject]);

  const invalidUniversity =
    route.kind === "subjectsByUniversity" &&
    !isLoading &&
    !error &&
    !universities.includes(route.university);
  const invalidTable =
    route.kind === "tableByUniversity" &&
    !isLoading &&
    !error &&
    (!universities.includes(route.university) ||
      !routeTableSubject);
  const invalidInstituteOverview =
    route.kind === "instituteOverview" &&
    !isLoading &&
    !error &&
    (originCodeResolved === null ||
      institutePageSections === null ||
      institutePageSections.length === 0);
  const invalidTableByOrigin =
    route.kind === "tableByOrigin" &&
    !isLoading &&
    !error &&
    (originCodeResolved === null || originSingleSection === null);
  const invalidOriginUniversityYearly =
    route.kind === "originUniversityYearly" &&
    !isLoading &&
    !error &&
    (originCodeResolved === null ||
      !universities.includes(route.receivingUniversity) ||
      !originUniversityYearSections ||
      originUniversityYearSections.length === 0);
  const invalidUniversityOriginDetail =
    route.kind === "universityOriginDetail" &&
    !isLoading &&
    !error &&
    (!universities.includes(route.university) ||
      !universityOriginYearSections ||
      universityOriginYearSections.length === 0);

  function LinkButton({
    to,
    className,
    children,
    title,
    ariaCurrent,
  }: {
    to: string;
    className?: string;
    children: ReactNode;
    title?: string;
    ariaCurrent?: boolean | "page" | "step" | "location" | "date" | "time" | "true" | "false";
  }) {
    return (
      <a
        href={to}
        className={className}
        title={title}
        aria-current={ariaCurrent}
        onClick={e => {
          e.preventDefault();
          navigate(to);
        }}
      >
        {children}
      </a>
    );
  }

  function handleActivationSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (activationInput.trim() !== ACTIVATION_CODE) {
      setActivationError("啟用碼不正確");
      return;
    }
    localStorage.setItem(ACTIVATION_STORAGE_KEY, ACTIVATION_CODE);
    setIsUnlocked(true);
    setActivationError("");
  }
  const routeNeedsActivation =
    route.kind === "tableByUniversity" ||
    route.kind === "instituteOverview" ||
    route.kind === "tableByOrigin" ||
    route.kind === "originUniversityYearly" ||
    route.kind === "universityOriginDetail" ||
    route.kind === "tranu";
  const routeHasRenderableData =
    (route.kind === "tableByUniversity" && !!tableModel && !invalidTable) ||
    (route.kind === "instituteOverview" &&
      !!institutePageSections &&
      institutePageSections.length > 0 &&
      !invalidInstituteOverview) ||
    (route.kind === "tableByOrigin" && !!originSingleSection && !invalidTableByOrigin) ||
    (route.kind === "originUniversityYearly" &&
      !!originUniversityYearSections &&
      originUniversityYearSections.length > 0 &&
      !invalidOriginUniversityYearly) ||
    (route.kind === "universityOriginDetail" &&
      !!universityOriginYearSections &&
      universityOriginYearSections.length > 0 &&
      !invalidUniversityOriginDetail) ||
    (route.kind === "tranu" && TRANSFER_ROWS.length > 0);
  const showActivationModal =
    routeNeedsActivation && routeHasRenderableData && (!activationReady || !isUnlocked);

  return (
    <SiteChrome
      gated={showActivationModal}
      overlay={
        showActivationModal ? (
        <div className="activation-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="activation-title">
          <section className="card nj-card activation-card activation-modal-card">
            <h2 id="activation-title" className="step-title">Non JUPAS 相關統計數字 - 輸入啟用碼</h2>
            <p className="uni-origin-summary-intro">
              請先關注 Non JUPAS 資訊頻道的 YouTube 頻道 <a href='https://www.youtube.com/@nonjupas' style={{ textDecoration: 'underline' }}>@nonjupas</a>，輸入啟用碼（免費），以瀏覽完整資料。
            </p>
            <p>
              <a
                className="browse-mode-link"
                href="https://www.youtube.com/@nonjupas"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fa-brands fa-youtube" aria-hidden="true" /> 前往 YouTube 頻道
              </a>
              </p>
              <p>
              <a
                className="browse-mode-link"
                href="https://t.me/nonjupas"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fa-brands fa-telegram" aria-hidden="true" /> Non JUPAS 討論區
              </a>
            </p>
            <form className="activation-form" onSubmit={handleActivationSubmit}>
              <label htmlFor="activation-code">啟用碼</label>
              <input
                id="activation-code"
                type="text"
                value={activationInput}
                onChange={e => setActivationInput(e.target.value)}
                placeholder="輸入啟用碼"
                autoComplete="one-time-code"
              />
              <div className="activation-form-actions">
                <button type="submit">啟用</button>
                <a
                  className="activation-btn-get-code"
                  href="https://www.youtube.com/@nonjupas"
                  target="_blank"
                  rel="noreferrer"
                >
                  獲取啟用碼
                </a>
              </div>
            </form>
            {!activationReady ? <p className="state">載入中...</p> : null}
            {activationError ? (
              <p className="state error">
                {activationError}，請先到 YouTube 取得免費啟用碼：
                <a
                  href="https://www.youtube.com/@nonjupas"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://www.youtube.com/@nonjupas
                </a>
              </p>
            ) : null}
          </section>
        </div>
        ) : null
      }
    >
      <nav aria-label="breadcrumb">
        <ol className="page-breadcrumb">
          <li>
            <a href={mainSiteUrl("/")}>Non JUPAS 資訊頻道</a>
          </li>
          {route.kind === "root" ? (
            <li>
              <span className="active">入學統計</span>
            </li>
          ) : (
            <li>
              <LinkButton to="/">入學統計</LinkButton>
            </li>
          )}
          {route.kind === "universityOriginSummary" ? (
            <li>
              <span className="active">
                {tab === "fyfd" ? "一年級" : "高年級"} · 按大學 · 原就讀院校分布
              </span>
            </li>
          ) : null}
          {route.kind === "universityOriginDetail" ? (
            <>
              <li>
                <LinkButton to={pathForUniversityOriginSummary(tab)}>
                  {tab === "fyfd" ? "一年級" : "高年級"} · 按大學 · 原就讀院校分布
                </LinkButton>
              </li>
              <li>
                <span className="active">{route.university}</span>
              </li>
            </>
          ) : null}
          {route.kind === "list" ? (
            <li>
              <span className="active">{listBrowseLabel(tab, route.mode)}</span>
            </li>
          ) : null}
          {route.kind === "tranu" ? (
            <li>
              <span className="active">本地生轉校人數（按大學及課程）</span>
            </li>
          ) : null}
          {route.kind === "subjectsByUniversity" || route.kind === "tableByUniversity" ? (
            <>
              <li>
                <LinkButton to={pathForList(tab, "by_university")}>
                  {listBrowseLabel(tab, "by_university")}
                </LinkButton>
              </li>
              {route.kind === "subjectsByUniversity" ? (
                <li>
                  <span className="active">{route.university}</span>
                </li>
              ) : (
                <>
                  <li>
                    <LinkButton to={pathForSubjectsByUniversity(tab, route.university)}>
                      {route.university}
                    </LinkButton>
                  </li>
                  <li>
                    <span className="active">{routeTableSubject || route.subjectKey}</span>
                  </li>
                </>
              )}
            </>
          ) : null}
          {route.kind === "instituteOverview" ||
          route.kind === "tableByOrigin" ||
          route.kind === "originUniversityYearly" ? (
            <>
              <li>
                <LinkButton to={pathForList(tab, "by_origin")}>
                  {listBrowseLabel(tab, "by_origin")}
                </LinkButton>
              </li>
              {route.kind === "instituteOverview" ? (
                <li>
                  <span className="active">{originLinkName || route.originName}</span>
                </li>
              ) : (
                <>
                  <li>
                    <LinkButton to={pathForInstituteOverview(tab, originLinkName || route.originName)}>
                      {originLinkName || route.originName}
                    </LinkButton>
                  </li>
                  {route.kind === "tableByOrigin" ? (
                    <li>
                      <span className="active">{route.receivingUniversity}</span>
                    </li>
                  ) : (
                    <>
                      <li>
                        <LinkButton
                          to={pathForTableByOrigin(
                            tab,
                            originLinkName || route.originName,
                            route.receivingUniversity,
                          )}
                        >
                          {route.receivingUniversity}
                        </LinkButton>
                      </li>
                      <li>
                        <span className="active">按年圖表</span>
                      </li>
                    </>
                  )}
                </>
              )}
            </>
          ) : null}
        </ol>
      </nav>
      <h1 className="page-title">{heading}</h1>
      <ResourceGrid />

      <section className="nj-card stats-card">

        {dataTab !== null && isLoading ? <p className="state">資料載入中...</p> : null}
        {dataTab !== null && error ? <p className="state error">無法載入資料：{error}</p> : null}

        {!isLoading && !error && route.kind === "root" ? (
          <div className="page-step">
            <h2 className="step-title">請選擇統計</h2>
            <ul className="link-grid">
              <li key="su-orig">
                <LinkButton to={pathForUniversityOriginSummary("senior")} className="link-tile">
                  高年級入學 · 按大學：原就讀院校分布（不分科目）
                </LinkButton>
              </li>
              <li key="tranu">
                <LinkButton to="/tranu" className="link-tile">
                  本地生轉校人數（2022/23 至 2024/25，按大學及課程）
                </LinkButton>
              </li>
              <li key="fy-orig">
                <LinkButton to={pathForUniversityOriginSummary("fyfd")} className="link-tile">
                  一年級入學 · 按大學：原就讀院校分布（不分科目）
                </LinkButton>
              </li>
              <li key="su">
                <LinkButton to={pathForList("senior", "by_university")} className="link-tile">
                  高年級學士學位經非聯招入學人數（以大學劃分）
                </LinkButton>
              </li>
              <li key="so">
                <LinkButton to={pathForList("senior", "by_origin")} className="link-tile">
                  高年級學士學位經非聯招入學人數（以原就讀院校劃分）
                </LinkButton>
              </li>
              <li key="fu">
                <LinkButton to={pathForList("fyfd", "by_university")} className="link-tile">
                  學士學位一年級經非聯招入學人數（以大學劃分）
                </LinkButton>
              </li>
              <li key="fo">
                <LinkButton to={pathForList("fyfd", "by_origin")} className="link-tile">
                  學士學位一年級經非聯招入學人數（以原就讀院校劃分）
                </LinkButton>
              </li>
            </ul>
          </div>
        ) : null}

        {!isLoading && !error && route.kind === "universityOriginSummary" ? (
          <div className="page-step">
            <h2 className="step-title">請選擇大學</h2>
            <p className="uni-origin-summary-intro">
              請選擇大學；每頁按<strong>學年</strong>顯示（不分科目），佔比以該學年該校總人數計算。圓餅圖上懸浮可查看名稱。
            </p>
            {universities.length > 0 ? (
              <ul className="link-grid">
                {universities.map(uni => (
                  <li key={uni}>
                    <LinkButton to={pathForUniversityOriginDetail(tab, uni)} className="link-tile">
                      {uni}
                    </LinkButton>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="state">暫無可顯示的資料。</p>
            )}
            <p className="footnote">標註：^ 為臨時數字。</p>
          </div>
        ) : null}

        {!isLoading && !error && route.kind === "universityOriginDetail" ? (
          invalidUniversityOriginDetail ? (
            <p className="state error">找不到此大學或沒有相關紀錄，請返回選擇大學。</p>
          ) : (
            <div className="page-step">
              <p className="uni-origin-summary-intro">
                以下按<strong>學年</strong>分段（不分科目）；佔比以該學年該校總人數計算。
              </p>
              {(universityOriginYearSections ?? []).map(ym => (
                <YearDistributionBlock
                  key={ym.year}
                  ym={ym}
                  firstColHeader="原就讀院校"
                  ariaPieLabel={`${route.university} ${ym.year}：原就讀院校人數分布，${ym.slices.length} 項來源，合共 ${ym.total} 人`}
                />
              ))}
              <p className="footnote">標註：^ 為臨時數字。</p>
            </div>
          )
        ) : null}

        {!isLoading && !error && route.kind === "list" && route.mode === "by_university" ? (
          <div className="page-step">
            <h2 className="step-title">請選擇大學</h2>
            <ul className="link-grid">
              {universities.map(uni => (
                <li key={uni}>
                  <LinkButton to={pathForSubjectsByUniversity(tab, uni)} className="link-tile">
                    {uni}
                  </LinkButton>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!isLoading && !error && route.kind === "list" && route.mode === "by_origin" ? (
          <div className="page-step">
            <h2 className="step-title">請選擇原就讀院校</h2>
            {institutesWithDataNames.length === 0 ? (
              <p className="state">暫無院校資料。</p>
            ) : (
              <ul className="link-grid">
                {institutesWithDataNames.map(name => (
                  <li key={name}>
                    <LinkButton to={pathForInstituteOverview(tab, name)} className="link-tile">
                      {name}
                    </LinkButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {!isLoading && !error && route.kind === "instituteOverview" ? (
          invalidInstituteOverview ? (
            <p className="state error">找不到此院校或沒有相關紀錄，請返回選擇院校。</p>
          ) : institutePageSections && institutePageSections.length > 0 ? (
            <div className="page-step">
              <p className="step-context">{originLinkName || route.originName}</p>
              {institutePageSections.map((section, idx) => (
                <section key={section.university} className={idx > 0 ? "institute-uni-section" : undefined}>
                  <h3 className="institute-uni-title">{section.university}</h3>
                  <p className="institute-section-permalink institute-section-permalink-row">
                    <LinkButton
                      to={pathForOriginUniversityYearly(
                        tab,
                        originLinkName || route.originName,
                        section.university,
                      )}
                      className="browse-mode-link"
                    >
                      <i className="fa-solid fa-chart-pie" aria-hidden="true" /> 按年圖表（各科占比）
                    </LinkButton>
                    <LinkButton
                      to={pathForTableByOrigin(tab, originLinkName || route.originName, section.university)}
                      className="browse-mode-link"
                    >
                      <i className="fa-solid fa-table" aria-hidden="true" />{" "}
                      {`原就讀${originLinkName || route.originName}入讀${section.university}的申請人（課程表）`}
                    </LinkButton>
                  </p>
                  <div className="table-wrap">
                    <table
                      className={
                        section.years.length === 1
                          ? "data-table data-table-single-year"
                          : "data-table"
                      }
                    >
                      <colgroup>
                        <col className="data-col-institute" />
                        {section.years.map(y => (
                          <col key={y} className="data-col-year" />
                        ))}
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col" className="sticky-col">
                            課程
                          </th>
                          {section.years.map(y => (
                            <th key={y} scope="col" className="data-col-year-header">
                              {y}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.subjects.map(sub => (
                          <tr key={sub}>
                            <th scope="row" className="sticky-col row-label">
                              {sub}
                            </th>
                            {section.years.map(y => (
                              <td key={y} className="num">
                                {(section.matrix[sub]?.[y] ?? 0) > 0 ? section.matrix[sub]?.[y] : "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="total-row">
                          <th scope="row" className="sticky-col row-label">
                            小計
                          </th>
                          {section.years.map(y => (
                            <td key={y} className="num">
                              {section.columnTotals[y] ?? 0}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
              <p className="footnote">標註：^ 為臨時數字。</p>
            </div>
          ) : (
            <p className="state">此院校沒有紀錄。</p>
          )
        ) : null}

        {!isLoading && !error && route.kind === "originUniversityYearly" ? (
          invalidOriginUniversityYearly ? (
            <p className="state error">找不到此組合或沒有相關紀錄，請返回上一步。</p>
          ) : (
            <div className="page-step">
              <p className="uni-origin-summary-intro">
                按<strong>學年</strong>顯示入讀「{route.receivingUniversity}」之<strong>課程</strong>人數及占比（僅計原就讀「
                {originLinkName || route.originName}」之非聯招人數）。
              </p>
              <p className="institute-section-permalink">
                <LinkButton
                  to={pathForTableByOrigin(
                    tab,
                    originLinkName || route.originName,
                    route.receivingUniversity,
                  )}
                  className="browse-mode-link"
                >
                  <i className="fa-solid fa-table" aria-hidden="true" /> 改以課程表（跨學年對照）瀏覽
                </LinkButton>
              </p>
              {(originUniversityYearSections ?? []).map(ym => (
                <YearDistributionBlock
                  key={ym.year}
                  ym={ym}
                  firstColHeader="課程"
                  ariaPieLabel={`${route.receivingUniversity} ${ym.year}：課程人數分布，${ym.slices.length} 項，合共 ${ym.total} 人`}
                />
              ))}
              <p className="footnote">標註：^ 為臨時數字。</p>
            </div>
          )
        ) : null}

        {!isLoading && !error && route.kind === "tableByOrigin" ? (
          invalidTableByOrigin ? (
            <p className="state error">找不到此入讀大學或沒有相關紀錄，請返回上一步。</p>
          ) : originSingleSection ? (
            <div className="page-step">
              <p className="step-context">
                {originLinkName || route.originName} · {route.receivingUniversity}
              </p>
              <p className="institute-section-permalink">
                <LinkButton
                  to={pathForOriginUniversityYearly(
                    tab,
                    originLinkName || route.originName,
                    route.receivingUniversity,
                  )}
                  className="browse-mode-link"
                >
                  <i className="fa-solid fa-chart-pie" aria-hidden="true" /> 改以按年圖表（各科占比）瀏覽
                </LinkButton>
              </p>
              <div className="table-wrap">
                <table
                  className={
                    originSingleSection.years.length === 1
                      ? "data-table data-table-single-year"
                      : "data-table"
                  }
                >
                  <colgroup>
                    <col className="data-col-institute" />
                    {originSingleSection.years.map(y => (
                      <col key={y} className="data-col-year" />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col" className="sticky-col">
                        課程
                      </th>
                      {originSingleSection.years.map(y => (
                        <th key={y} scope="col" className="data-col-year-header">
                          {y}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {originSingleSection.subjects.map(sub => (
                      <tr key={sub}>
                        <th scope="row" className="sticky-col row-label">
                          {sub}
                        </th>
                        {originSingleSection.years.map(y => (
                          <td key={y} className="num">
                            {(originSingleSection.matrix[sub]?.[y] ?? 0) > 0
                              ? originSingleSection.matrix[sub]?.[y]
                              : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="total-row">
                      <th scope="row" className="sticky-col row-label">
                        小計
                      </th>
                      {originSingleSection.years.map(y => (
                        <td key={y} className="num">
                          {originSingleSection.columnTotals[y] ?? 0}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="footnote">標註：^ 為臨時數字。</p>
            </div>
          ) : (
            <p className="state">此組合沒有紀錄。</p>
          )
        ) : null}

        {!isLoading && !error && route.kind === "subjectsByUniversity" ? (
          invalidUniversity ? (
            <p className="state error">找不到此大學，請返回選擇大學。</p>
          ) : (
            <div className="page-step">
              <h2 className="step-title">請選擇課程</h2>
              <p className="step-context">{route.university}</p>
              {subjects.length === 0 ? (
                <p className="state">此大學暫無課程資料。</p>
              ) : (
                <ul className="link-grid">
                  {subjects.map(sub => (
                    <li key={sub}>
                      <LinkButton
                        to={pathForTableByUniversity(
                          tab,
                          route.university,
                          subjectSlugByName.get(sub) ?? slugifyAscii(sub),
                        )}
                        className="link-tile"
                      >
                        {sub}
                      </LinkButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        ) : null}

        {!isLoading && !error && route.kind === "tableByUniversity" ? (
          invalidTable ? (
            <p className="state error">找不到此大學或課程，請返回上一步。</p>
          ) : tableModel ? (
            <div className="page-step">
              <p className="step-context">
                {route.university} · {routeTableSubject || route.subjectKey}
              </p>
              <div className="table-wrap">
                <table
                  className={
                    tableModel.years.length === 1 ? "data-table data-table-single-year" : "data-table"
                  }
                >
                  <colgroup>
                    <col className="data-col-institute" />
                    {tableModel.years.map(y => (
                      <col key={y} className="data-col-year" />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col" className="sticky-col">
                        原就讀院校
                      </th>
                      {tableModel.years.map(y => (
                        <th key={y} scope="col" className="data-col-year-header">
                          {y}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableModel.codes.map(code => (
                      <tr key={code}>
                        <th scope="row" className="sticky-col row-label">
                          {instituteMap[code] ?? `院校 ${code}`}
                        </th>
                        {tableModel.years.map(y => (
                          <td key={y} className="num">
                            {(tableModel.matrix[code]?.[y] ?? 0) > 0 ? tableModel.matrix[code]?.[y] : "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="total-row">
                      <th scope="row" className="sticky-col row-label">
                        總計
                      </th>
                      {tableModel.years.map(y => (
                        <td key={y} className="num">
                          {tableModel.columnTotals[y] ?? 0}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="footnote">標註：^ 為臨時數字。</p>
            </div>
          ) : (
            <p className="state">此課程沒有紀錄。</p>
          )
        ) : null}

        {route.kind === "tranu" ? (
          <div className="page-step">
            <p className="uni-origin-summary-intro">
              資料範圍為 2022/23 至 2024/25 學年。根據教資會統計定義，「轉校人數」指原就讀於教資會資助課程但未有完成相關課程，並在一年內獲其他教資會資助課程（不包括原就讀大學）取錄的學生人數。
            </p>
            {(["2024/25", "2023/24", "2022/23"] as const).map(year => (
              <TranuYearBlock key={year} year={year} rows={tranuByYear[year] ?? []} />
            ))}
            <p className="footnote">總計：{tranuOverallTotal} 人。</p>
          </div>
        ) : null}
      </section>

      <div className="api-notes">
        <section className="nj-card api-note-card">
          <p>
            覺得 Asso 升學資訊好唔透明、好迷茫？大學每科取錄邊間副學位院校嘅人數較多？我哋整合來自教資會嘅記錄，等你可以一眼睇晒過往唔同科到底收咗幾多副學位學生。
          </p>
          <p>
            有咗呢啲具體數據，你就可以更精準咁制定報考策略，唔使再盲摸摸亂投一通。數據先係最好嘅導師，即刻善用呢個工具，好好規劃你嘅大學銜接之路，大幅提升成功「上岸」嘅機率啦！
          </p>
        </section>
        <section className="nj-card api-note-card">
          <p>資料來源：大學教育資助委員會</p>
          <p>
            資料只作一般參考用途，不應被視為有關資料的詳盡及具權威性的版本。雖然我們力求準確編製內容，但並不保證其準確性、可靠性或完整性。
          </p>
        </section>
      </div>
    </SiteChrome>
  );
}

export default App;
